import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { CarCard } from "@/components/site/CarCard";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/Eyebrow";
import { RentalRequirements } from "@/components/site/RentalRequirements";
import { ServiceAreas } from "@/components/site/ServiceAreas";
import { GoogleReviews } from "@/components/site/GoogleReviews";
import { cars } from "@/lib/cars";
import { ArrowRight } from "lucide-react";
import { GOOGLE_BUSINESS_URL, RATING_VALUE, REVIEW_COUNT } from "@/lib/schema";
import { MobileRateCard } from "@/components/site/MobileRateCard";
import hero from "@/assets/hero-escalade-wide.jpg";
import mobileHero from "@/assets/hero-escalade-tall-sky.webp";
import ogHome from "@/assets/og-home.jpg.asset.json";
import HeroSection from "@/components/site/HeroSection";

const OG_HOME_URL = `https://avoluscars.com${ogHome.url}`;
import { WhyAvolus } from "@/components/site/WhyAvolus";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Luxury & Exotic Car Rental Vancouver | Avolus Cars" },
      {
        name: "description",
        content:
          "Luxury and exotic car rental in Vancouver with Avolus Cars. Choose BMW, Mercedes, Range Rover, Porsche or Cadillac, with free delivery in Vancouver and to YVR.",
      },
      { name: "keywords", content: "car rental Vancouver, car rental in Vancouver, west coast car rental, west coast rental car, luxury car rental Vancouver, exotic car rental Vancouver, YVR car rental, wedding car rental Vancouver, Whistler luxury rental" },
      { property: "og:title", content: "Luxury & Exotic Car Rental Vancouver | Avolus Cars" },
      {
        property: "og:description",
        content:
          "Luxury and exotic car rental in Vancouver with Avolus Cars. Choose BMW, Mercedes, Range Rover, Porsche or Cadillac, with free delivery in Vancouver and to YVR.",
      },
      { property: "og:url", content: "https://avoluscars.com/" },
       { property: "og:type", content: "website" },
       { name: "twitter:card", content: "summary_large_image" },
       { property: "og:image", content: OG_HOME_URL },
       { name: "twitter:image", content: OG_HOME_URL },
    ],
    links: [
      { rel: "canonical", href: "https://avoluscars.com/" },
      { rel: "preload", as: "image", href: hero, media: "(min-width: 768px)", fetchpriority: "high" },
      { rel: "preload", as: "image", href: mobileHero, media: "(max-width: 767px)", fetchpriority: "high" },
    ],
    // AutoRental JSON-LD is emitted once at the root; no duplicate here.
  }),
  component: Home,
});

function Home() {
  return (
    <Layout showWhyAvolus={false}>
      <div className="home-sections flex flex-col">
      <HeroSection desktopImage={hero} mobileImage={mobileHero} rating={RATING_VALUE} reviewCount={REVIEW_COUNT} googleUrl={GOOGLE_BUSINESS_URL} />

      {/* Showcase: full fleet */}
      <section className="pt-12 pb-20 lg:pt-16 lg:pb-28">
        <div className="mx-auto max-w-[110rem] px-4 sm:px-6 lg:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
              <div>
                <Eyebrow>Featured Vehicles</Eyebrow>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-foreground max-w-2xl">
                  A fleet without compromise.
                </h2>
              </div>
              <Link
                to="/fleet"
                className="text-xs uppercase tracking-[0.25em] text-foreground border-b border-foreground/40 hover:border-foreground pb-1 self-start md:self-end"
              >
                View entire fleet →
              </Link>
            </div>
          </Reveal>

          <div className="grid gap-6 lg:gap-8 md:grid-cols-2 lg:grid-cols-3">
            {cars.map((c, i) => (
              <CarCard key={c.slug} car={c} index={i} />
            ))}
          </div>
        </div>
      </section>

      <GoogleReviews />

      <ServiceAreas />

      <div className="md:order-5"><RentalRequirements /></div>

      {/* Why Avolus */}
      <div className="md:order-3"><WhyAvolus /></div>

      {/* Rates summary */}
      <section className="hidden md:block py-10 md:py-24 lg:py-28 bg-muted/40 border-y border-border md:order-4">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <div>
                <Eyebrow>Rental Rates</Eyebrow>
                <h2 className="font-display text-4xl md:text-5xl text-foreground max-w-2xl">
                  Transparent pricing.
                </h2>
                <p className="mt-3 text-sm text-muted-foreground">All rates in Canadian dollars (CAD).</p>
              </div>
              <Link
                to="/rates"
                className="text-xs uppercase tracking-[0.25em] text-foreground border-b border-foreground/40 hover:border-foreground pb-1 self-start md:self-end"
              >
                View full rate sheet →
              </Link>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="space-y-5 md:hidden">
              {cars.map((car) => <MobileRateCard key={car.slug} car={car} />)}
            </div>
            <div className="hidden md:block border border-border bg-card overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-5 py-4 text-[10px] uppercase tracking-[0.25em] text-foreground/80 font-medium">
                      Vehicle
                    </th>
                    <th className="text-left px-5 py-4 text-[10px] uppercase tracking-[0.25em] text-foreground/80 font-medium">24 Hours</th>
                    <th className="text-left px-5 py-4 text-[10px] uppercase tracking-[0.25em] text-foreground/80 font-medium">48 Hours</th>
                    <th className="text-left px-5 py-4 text-[10px] uppercase tracking-[0.25em] text-foreground/80 font-medium">3 - 6 Days</th>
                    <th className="text-left px-5 py-4 text-[10px] uppercase tracking-[0.25em] text-foreground/80 font-medium">7+ Days</th>
                  </tr>
                </thead>
                <tbody>
                  {cars.map((c, i) => (
                    <tr key={c.slug} className={`border-b border-border last:border-b-0 ${i % 2 === 1 ? "bg-background/40" : ""}`}>
                      <td className="px-5 py-4">
                        <Link to="/fleet/$slug" params={{ slug: c.slug }} className="font-display text-base text-foreground hover:underline underline-offset-4">
                          {c.name}
                        </Link>
                      </td>
                      <td className="px-5 py-4 text-foreground">{c.rates.h24}</td>
                      <td className="px-5 py-4 text-foreground/80">{c.rates.h48}</td>
                      <td className="px-5 py-4 text-foreground/80">{c.rates.d36}</td>
                      <td className="px-5 py-4 text-foreground/80">{c.rates.d7}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      <p className="hidden md:block md:order-9 pb-16 text-center text-xs text-muted-foreground">
        Own a luxury vehicle?{" "}
        <Link to="/rent-out-your-car" className="underline underline-offset-4 hover:text-foreground transition-colors">
          Rent it out with Avolus
        </Link>
      </p>
      </div>
    </Layout>
  );
}
